import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import take from "lodash/take";
import trim from "lodash/trim";
import { useEffect, useMemo } from "react";
import { SEARCH_RESULTS_LIMIT } from "../../../api/features/product-search/product-search-query-builder";
import { useProductSearchQuery } from "../../../api/features/product-search/useProductSearch.query";
import { useProductsLeanQuery } from "../../../api/features/products/useProductsLean.query";
import { useRecentProductsQuery } from "../../../api/features/search-history/useRecentProducts.query";
import { MINLENGTH_TO_SEARCH } from "../../../constants/env";
import useDebounce from "../../../legacy/hooks/useDebounce";
import type { Search } from "../../../legacy/interfaces/Search";
import type { IProductLean } from "../../../legacy/pages/ProductsListPage/entities/product-lean/product-lean.entity.interface";
import { serviceCollectionFilter } from "../../../legacy/services/serviceCollectionFilter";
import { sendSearchGtmEvent } from "../../../legacy/stores/search/gtm-event";
import { useCitizenGeoContext } from "./useCitizenGeoContext";

const SEARCH_DEBOUNCE_TIME_MILLIS = 250;
const POPULAR_PRODUCTS_LIMIT = 5;

// shared across hook instances: one GTM event per search
let lastTrackedSearchText = "";

export interface IUseProductSearchResults {
  results: Search.ResultsCollection;
  loading: boolean;
}

export function useProductSearchResults(searchText: string, enabled: boolean = true): IUseProductSearchResults {
  const debouncedSearchText = useDebounce(trim(searchText), SEARCH_DEBOUNCE_TIME_MILLIS);
  const isAcceptedSearchText = enabled && debouncedSearchText.length >= MINLENGTH_TO_SEARCH;
  const acceptedSearchText = isAcceptedSearchText ? debouncedSearchText : "";

  const geoContext = useCitizenGeoContext(enabled);
  const searchQuery = useProductSearchQuery(acceptedSearchText, geoContext);
  const { orderedProducts, loading: isCatalogLoading } = useProductsLeanQuery(enabled);
  const { recentProducts, loading: isRecentsLoading } = useRecentProductsQuery(enabled);

  useEffect(
    function trackSearch() {
      trackSearchOnce(acceptedSearchText);
    },
    [acceptedSearchText]
  );

  const foundProducts = useMemo(
    function toFoundProducts(): IProductLean[] {
      const isEmptySearchText = isEmpty(acceptedSearchText);
      if (isEmptySearchText) {
        return orderedProducts;
      }

      if (searchQuery.error) {
        const fallbackProducts = serviceCollectionFilter(orderedProducts, {
          filter: "",
          textInput: acceptedSearchText,
        });
        return take(fallbackProducts, SEARCH_RESULTS_LIMIT);
      }

      return pickRankedProducts(searchQuery.rankedUniqueIdentifiers ?? [], orderedProducts);
    },
    [acceptedSearchText, orderedProducts, searchQuery.rankedUniqueIdentifiers, searchQuery.error]
  );

  const results = useMemo(
    function toResultsCollection(): Search.ResultsCollection {
      return {
        searchText: acceptedSearchText,
        foundProducts,
        popularProducts: take(orderedProducts, POPULAR_PRODUCTS_LIMIT),
        recentProducts,
      };
    },
    [acceptedSearchText, foundProducts, orderedProducts, recentProducts]
  );

  return { results, loading: searchQuery.loading || isCatalogLoading || isRecentsLoading };
}

function pickRankedProducts(rankedIdentifiers: string[], products: IProductLean[]): IProductLean[] {
  const productsByIdentifier = keyBy(products, "uniqueIdentifier");

  const rankedProducts = rankedIdentifiers.map(function toProduct(identifier) {
    return productsByIdentifier[identifier];
  });

  return take(compact(rankedProducts), SEARCH_RESULTS_LIMIT);
}

function trackSearchOnce(searchText: string): void {
  if (searchText === lastTrackedSearchText) {
    return;
  }

  lastTrackedSearchText = searchText;
  sendSearchGtmEvent({ searchText });
}
